import { EnglishLevel } from '../enum.js';

// Standard values of the candidate CV (Özgeçmişim) fields, shared by the form (pickers), the server
// schema (refuses anything else) and the CV upload (turns what the parser read into them).
//
// An experience date is a month ("2020-03") or, when only the year is known, a year ("2020"); the
// end left empty means the job is still going on. A graduation year is four digits in a fixed range.
// A language level is one of the levels a position asks for (EnglishLevel without NO_ENGLISH).

// "Kısaca kendinden bahset": a short introduction, not a cover letter.
export const CV_SUMMARY_MAX_LENGTH = 500;

export const CV_MONTH_PATTERN = /^\d{4}(-(0[1-9]|1[0-2]))?$/;

export const CV_YEAR_PATTERN = /^\d{4}$/;

export const CV_FIRST_YEAR = 1950;

// Graduation can be planned a few years ahead (a student still studying).
export const CV_GRADUATION_YEARS_AHEAD = 8;

export const CANDIDATE_LANGUAGE_LEVELS = [
  EnglishLevel.BEGINNER,
  EnglishLevel.INTERMEDIATE,
  EnglishLevel.UPPER_INTERMEDIATE,
  EnglishLevel.ADVANCED,
  EnglishLevel.FLUENT,
] as const;

export type CandidateLanguageLevel = (typeof CANDIDATE_LANGUAGE_LEVELS)[number];

// Month names (and short forms) a CV may write a date with: Turkish, English, German, French, Greek.
const MONTH_WORDS: ReadonlyArray<readonly string[]> = [
  ['ocak', 'oca', 'january', 'jan', 'januar', 'janvier', 'janv', 'ιανουάριος', 'ιαν'],
  ['şubat', 'şub', 'subat', 'february', 'feb', 'februar', 'février', 'fevrier', 'févr', 'φεβρουάριος', 'φεβ'],
  ['mart', 'mar', 'march', 'märz', 'marz', 'mars', 'μάρτιος', 'μαρ'],
  ['nisan', 'nis', 'april', 'apr', 'avril', 'avr', 'απρίλιος', 'απρ'],
  ['mayıs', 'may', 'mayis', 'mai', 'μάιος', 'μαΐ'],
  ['haziran', 'haz', 'june', 'jun', 'juni', 'juin', 'ιούνιος', 'ιουν'],
  ['temmuz', 'tem', 'july', 'jul', 'juli', 'juillet', 'juil', 'ιούλιος', 'ιουλ'],
  ['ağustos', 'ağu', 'agustos', 'august', 'aug', 'août', 'aout', 'αύγουστος', 'αυγ'],
  ['eylül', 'eyl', 'eylul', 'september', 'sep', 'sept', 'septembre', 'σεπτέμβριος', 'σεπ'],
  ['ekim', 'eki', 'october', 'oct', 'oktober', 'okt', 'octobre', 'οκτώβριος', 'οκτ'],
  ['kasım', 'kas', 'kasim', 'november', 'nov', 'novembre', 'νοέμβριος', 'νοε'],
  ['aralık', 'ara', 'aralik', 'december', 'dec', 'dezember', 'dez', 'décembre', 'decembre', 'déc', 'δεκέμβριος', 'δεκ'],
];

const pad = (month: number) => String(month).padStart(2, '0');

const monthFromWord = (word: string): number | null => {
  // The word comes lower-cased by the Turkish rule; "ıan" (from "IAN") is tried as "ian" too.
  const index = MONTH_WORDS.findIndex((names) => names.includes(word) || names.includes(word.replace(/ı/g, 'i')));
  return index === -1 ? null : index + 1;
};

/**
 * A date as written in a CV or an older profile ("03.2020", "3/2020", "2020-03", "Mart 2020",
 * "Mar 2020", "2020") as a standard value ("2020-03", "2020"), or null when it reads as none.
 */
export const normalizeCvMonth = (value: string | null | undefined): string | null => {
  const text = value?.trim().toLocaleLowerCase('tr');
  if (!text) return null;
  const valid = (year: number, month: number | null) =>
    year >= CV_FIRST_YEAR && year <= 2100 && (month === null || (month >= 1 && month <= 12))
      ? month === null
        ? String(year)
        : `${year}-${pad(month)}`
      : null;

  let match = /^(\d{4})[-./](\d{1,2})(?:[-./]\d{1,2})?$/.exec(text);
  if (match) return valid(Number(match[1]), Number(match[2]));
  match = /^(?:\d{1,2}[-./])?(\d{1,2})[-./](\d{4})$/.exec(text);
  if (match) return valid(Number(match[2]), Number(match[1]));
  match = /^(\p{L}+)\.?\s+(\d{4})$/u.exec(text);
  if (match) {
    const month = monthFromWord(match[1]);
    return month ? valid(Number(match[2]), month) : null;
  }
  match = /^(\d{4})$/.exec(text);
  if (match) return valid(Number(match[1]), null);
  return null;
};

/** Year and month (null: year only) of a standard date. */
export const parseCvMonth = (value: string): { year: number; month: number | null } => {
  const [year, month] = value.split('-');
  return { year: Number(year), month: month ? Number(month) : null };
};

/** "2020-03" of a year and a month (null: the year alone). */
export const cvMonthValue = (year: number, month: number | null): string =>
  month === null ? String(year) : `${year}-${pad(month)}`;

/** The current month as a standard date. */
export const currentCvMonth = (now: Date = new Date()): string => cvMonthValue(now.getFullYear(), now.getMonth() + 1);

// Compared as numbers; a year alone counts as its first month for a start, its last for an end.
const monthIndex = (value: string, edge: 'start' | 'end'): number => {
  const { year, month } = parseCvMonth(value);
  return year * 12 + (month ?? (edge === 'start' ? 1 : 12));
};

/** What is wrong with an experience's dates, or null: a start in the future, an end before it. */
export const cvDateRangeError = (
  start: string | null | undefined,
  end: string | null | undefined,
  now: Date = new Date()
): 'START_IN_FUTURE' | 'END_BEFORE_START' | 'END_IN_FUTURE' | null => {
  const current = monthIndex(currentCvMonth(now), 'start');
  if (start && CV_MONTH_PATTERN.test(start) && monthIndex(start, 'start') > current) return 'START_IN_FUTURE';
  if (end && CV_MONTH_PATTERN.test(end)) {
    if (monthIndex(end, 'start') > current) return 'END_IN_FUTURE';
    if (start && CV_MONTH_PATTERN.test(start) && monthIndex(end, 'end') < monthIndex(start, 'start'))
      return 'END_BEFORE_START';
  }
  return null;
};

/** The years a graduation can be in, newest first. */
export const cvGraduationYears = (now: Date = new Date()): number[] => {
  const last = now.getFullYear() + CV_GRADUATION_YEARS_AHEAD;
  return Array.from({ length: last - CV_FIRST_YEAR + 1 }, (_, i) => last - i);
};

export const isCvGraduationYear = (value: string, now: Date = new Date()): boolean =>
  CV_YEAR_PATTERN.test(value) &&
  Number(value) >= CV_FIRST_YEAR &&
  Number(value) <= now.getFullYear() + CV_GRADUATION_YEARS_AHEAD;

/** A graduation year as written ("2019", "06/2019", "2015 - 2019"), or null. */
export const normalizeCvGraduationYear = (value: string | number | null | undefined, now: Date = new Date()): string | null => {
  const years = String(value ?? '').match(/\d{4}/g);
  const year = years?.at(-1);
  return year && isCvGraduationYear(year, now) ? year : null;
};

// Level words a CV or an older profile may use (CEFR included), lower-cased, per level.
const LEVEL_WORDS: ReadonlyArray<[CandidateLanguageLevel, RegExp]> = [
  [EnglishLevel.FLUENT, /\b(c2)\b|akıcı|akici|anadil|ana dil|native|mother tongue|fluent|fließend|muttersprache|courant|langue maternelle|bilingue|μητρική|άπταιστα/u],
  [EnglishLevel.UPPER_INTERMEDIATE, /\b(b2)\b|orta.?ileri|upper.?intermediate|obere mittelstufe|gute kenntnisse|intermédiaire.?(supérieur|avancé)|ανώτερο/u],
  [EnglishLevel.ADVANCED, /\b(c1)\b|ileri|advanced|proficient|fortgeschritten|sehr gut|verhandlungssicher|avancé|avance|προχωρημένο/u],
  [EnglishLevel.INTERMEDIATE, /\b(b1)\b|orta|intermediate|mittel|intermédiaire|intermediaire|μέτριο|μεσαίο/u],
  [EnglishLevel.BEGINNER, /\b(a1|a2)\b|başlangıç|baslangic|temel|beginner|elementary|basic|grundkenntnisse|anfänger|débutant|debutant|notions|αρχάριο|βασικό/u],
];

/** A language level as written ("İleri", "B2", "Native", "ADVANCED") as one of the levels, or null. */
export const normalizeCandidateLanguageLevel = (value: string | null | undefined): CandidateLanguageLevel | null => {
  if (!value?.trim()) return null;
  if ((CANDIDATE_LANGUAGE_LEVELS as readonly string[]).includes(value)) return value as CandidateLanguageLevel;
  // Lower-cased both ways: the Turkish rule keeps "İleri" whole but turns the I of "Intermediate" into ı.
  const texts = [value.trim().toLocaleLowerCase('tr'), value.trim().toLowerCase()];
  return LEVEL_WORDS.find(([, pattern]) => texts.some((text) => pattern.test(text)))?.[0] ?? null;
};
